import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  Modal,
  Switch,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  caseFields,
  clientFields,
  reportFields,
  reportStrings as strings,
} from "../../resources/strings/reports";
import { Button } from "../general/Button";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedFields: (keyof typeof reportFields)[];
  setSelectedFields: (fields: (keyof typeof reportFields)[]) => void;
}

export const ReportFieldSelectorModal = ({
  open,
  onClose,
  selectedFields,
  setSelectedFields,
}: Props) => {
  const [tempSelectedFields, setTempSelectedFields] = useState(selectedFields);

  const isClientSectionSelected = Object.keys(clientFields).every((key) =>
    tempSelectedFields.includes(key as keyof typeof reportFields)
  );
  const isCaseSectionSelected = Object.keys(caseFields).every((key) =>
    tempSelectedFields.includes(key as keyof typeof reportFields)
  );

  const handleToggle = (field: keyof typeof reportFields) => {
    setTempSelectedFields((prev) =>
      prev.includes(field)
        ? prev.filter((item) => item !== field)
        : [...prev, field]
    );
  };

  const handleSectionToggle = (section: "client" | "case") => {
    if (section === "client") {
      const clientKeys = Array.from(
        Object.keys(clientFields)
      ) as (keyof typeof reportFields)[];
      setTempSelectedFields((prev) =>
        isClientSectionSelected
          ? prev.filter((f) => !clientKeys.includes(f))
          : Array.from(new Set([...prev, ...clientKeys]))
      );
    } else if (section === "case") {
      const caseKeys = Array.from(
        Object.keys(caseFields)
      ) as (keyof typeof reportFields)[];
      setTempSelectedFields((prev) =>
        isCaseSectionSelected
          ? prev.filter((f) => !caseKeys.includes(f))
          : Array.from(new Set([...prev, ...caseKeys]))
      );
    }
  };

  const handleApply = () => {
    setSelectedFields(tempSelectedFields);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          height: 500,
          bgcolor: "background.paper",
          p: 4,
          borderRadius: 2,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "hidden",
        }}
      >
        <Typography variant="h6">{strings.labels.selectFields}</Typography>
        <Box sx={{ flex: 1, overflowY: "auto", paddingRight: 1 }}>
          <Box mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={isClientSectionSelected}
                  onChange={() => handleSectionToggle("client")}
                />
              }
              label={
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {strings.labels.clientFields}
                </Typography>
              }
            />
            <Grid container spacing={2}>
              {Object.entries(clientFields).map(([key, label]) => (
                <Grid item xs={6} key={key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tempSelectedFields.includes(
                          key as keyof typeof reportFields
                        )}
                        onChange={() =>
                          handleToggle(key as keyof typeof reportFields)
                        }
                      />
                    }
                    label={label}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isCaseSectionSelected}
                  onChange={() => handleSectionToggle("case")}
                />
              }
              label={
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {strings.labels.caseFields}
                </Typography>
              }
            />
            <Grid container spacing={2}>
              {Object.entries(caseFields).map(([key, label]) => (
                <Grid item xs={6} key={key}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tempSelectedFields.includes(
                          key as keyof typeof reportFields
                        )}
                        onChange={() =>
                          handleToggle(key as keyof typeof reportFields)
                        }
                      />
                    }
                    label={label}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            label={strings.labels.cancel}
          />
          <Button
            variant="contained"
            onClick={handleApply}
            label={strings.labels.apply}
          />
        </Box>
      </Box>
    </Modal>
  );
};
